import {
  AccountTree,
  Add,
  Analytics,
  Refresh,
  Settings,
  TrendingUp
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import { navigate } from '@reach/router';
import { useCallback, useEffect, useEffect as useReactEffect, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { api } from '../api/client';
import Layout from '../components/Layout';
import PipelineStage from '../components/PipelineStage';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';

const PipelinePage = () => {
  const { user, loading: authLoading, currentOrg } = useAuth();

  // ✅ Подключение к SSE для real-time обновлений
  useRealtimeSSE();
  const [pipeline, setPipeline] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [stageDeals, setStageDeals] = useState({});
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dealDialog, setDealDialog] = useState({ open: false, deal: null, stageId: null });
  const [pipelineDialog, setPipelineDialog] = useState({ open: false, mode: 'create' });
  const [pipelineForm, setPipelineForm] = useState({ name: '', description: '', isDefault: false });
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsFilters, setAnalyticsFilters] = useState({ dateFrom: '', dateTo: '' });
  const [stageDialog, setStageDialog] = useState({ open: false, mode: 'create', stageId: null });
  const [stageForm, setStageForm] = useState({ name: '', probability: 0, description: '' });
  const [dealFormData, setDealFormData] = useState({
    title: '',
    leadId: '',
    client_id: '',
    product_id: '',
    quantity: 1,
    amount: 0,
    status: 'new'
  });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Проверка авторизации
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    // Ждём, пока определится текущая организация (org_id), иначе запросы дадут 403
    if (user && currentOrg) {
      Promise.all([loadPipelines(), loadSupportData()]).then(() => {
        // если после загрузки списка ещё нет выбранной воронки — грузим дефолтную
        if (!pipeline) {
          loadPipelineData();
        }
      });
    }
  }, [user, authLoading, currentOrg]);

  const loadPipelineData = async (pipelineId = null) => {
    try {
      setLoading(true);
      setError('');

      let pipelineData;

      if (pipelineId) {
        // Загружаем конкретную воронку по ID
        const pipelineResponse = await api.get(`/pipelines/${pipelineId}`);
        pipelineData = pipelineResponse.data;
      } else {
        // Получаем дефолтную воронку
        const pipelineResponse = await api.get('/pipelines/default');
        pipelineData = pipelineResponse.data;
      }

      setPipeline(pipelineData);
      setStages(pipelineData.stages || []);
      // синхонизируем выбранную воронку в выпадающем списке
      setPipelines((prev) => {
        if (!prev || prev.length === 0) return prev;
        return prev.map(p => p.id === pipelineData.id ? { ...p } : p);
      });

      // Получаем сделки воронки
      if (pipelineData.id) {
        const dealsResponse = await api.get(`/pipelines/${pipelineData.id}/deals`);
        setStageDeals(dealsResponse.data.stages || {});
      }

    } catch (err) {
      console.error('Ошибка при загрузке воронки:', err);
      if (err.response?.status === 403 && !pipelineId) {
        // Скорее всего ещё не установился X-Organization-Id; подождём и попробуем снова
        setTimeout(() => loadPipelineData(pipelineId), 300);
        return;
      }
      if (err.response?.status === 404 && !pipelineId) {
        // Создаем дефолтную воронку если её нет
        await createDefaultPipeline();
      } else {
        setError('Не удалось загрузить воронку продаж');
        showSnackbar('Ошибка при загрузке данных воронки', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const createDefaultPipeline = async () => {
    try {
      const response = await api.get('/pipelines/default');
      const pipelineData = response.data;

      setPipeline(pipelineData);
      setStages(pipelineData.stages || []);
      setStageDeals({});

      showSnackbar('Загружена базовая воронка продаж', 'success');
    } catch (err) {
      console.error('Ошибка при получении дефолтной воронки:', err);
      setError('Не удалось получить воронку продаж');
      showSnackbar('Ошибка при загрузке воронки', 'error');
    }
  };

  const loadSupportData = async () => {
    try {
      const [clientsResponse, productsResponse, leadsResponse] = await Promise.all([
        api.get('/clients'),
        api.get('/products'),
        api.get('/leads')
      ]);

      const clientsData = Array.isArray(clientsResponse.data)
        ? clientsResponse.data
        : (clientsResponse.data && Array.isArray(clientsResponse.data.data) ? clientsResponse.data.data : []);
      const productsData = Array.isArray(productsResponse.data)
        ? productsResponse.data
        : (productsResponse.data && Array.isArray(productsResponse.data.data) ? productsResponse.data.data : []);
      const leadsData = Array.isArray(leadsResponse.data)
        ? leadsResponse.data
        : (leadsResponse.data && Array.isArray(leadsResponse.data.data) ? leadsResponse.data.data : []);

      setClients(clientsData);
      setProducts(productsData);
      setLeads(leadsData);
    } catch (err) {
      console.error('Ошибка при загрузке вспомогательных данных:', err);
    }
  };

  const loadPipelines = async () => {
    try {
      const res = await api.get('/pipelines');
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setPipelines(list);
    } catch (e) {
      console.error('Ошибка загрузки воронок:', e);
    }
  };

  const handlePipelineSelect = async (e) => {
    const id = e.target.value;
    await loadPipelineData(id);
  };

  const openCreatePipeline = () => {
    setPipelineForm({ name: '', description: '', isDefault: false });
    setPipelineDialog({ open: true, mode: 'create' });
  };

  const openEditPipeline = () => {
    if (!pipeline) return;
    setPipelineForm({ name: pipeline.name || '', description: pipeline.description || '', isDefault: !!pipeline.isDefault });
    setPipelineDialog({ open: true, mode: 'edit' });
  };

  const handlePipelineFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPipelineForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePipelineSubmit = async (e) => {
    e.preventDefault();
    try {
      if (pipelineDialog.mode === 'create') {
        const resp = await api.post('/pipelines', {
          name: pipelineForm.name?.trim() || 'Новая воронка',
          description: pipelineForm.description || '',
          isDefault: !!pipelineForm.isDefault,
          stages: []
        });
        await loadPipelines();
        await loadPipelineData(resp.data?.id);
        showSnackbar('Воронка создана', 'success');
      } else {
        await api.patch(`/pipelines/${pipeline.id}`, {
          name: pipelineForm.name?.trim() || pipeline.name,
          description: pipelineForm.description || '',
          isDefault: !!pipelineForm.isDefault
        });
        await loadPipelines();
        await loadPipelineData(pipeline.id);
        showSnackbar('Воронка обновлена', 'success');
      }
      setPipelineDialog({ open: false, mode: 'create' });
    } catch (e) {
      console.error(e);
      showSnackbar('Ошибка сохранения воронки', 'error');
    }
  };

  const handlePipelineDelete = async () => {
    if (!pipeline) return;
    if (!window.confirm('Удалить текущую воронку?')) return;
    try {
      await api.delete(`/pipelines/${pipeline.id}`);
      await loadPipelines();
      // после удаления загрузим дефолтную/первую
      const def = pipelines.find(p => p.isDefault) || pipelines[0];
      await loadPipelineData(def?.id);
      showSnackbar('Воронка удалена', 'success');
      setPipelineDialog({ open: false, mode: 'create' });
    } catch (e) {
      showSnackbar('Не удалось удалить воронку (возможно, это дефолтная)', 'error');
    }
  };

  const handleDealDrop = async (dealId, fromStageId, toStageId) => {
    try {
      // Оптимистично обновляем UI
      const updatedStageDeals = { ...stageDeals };

      // Находим сделку в старом этапе
      const fromStage = updatedStageDeals[fromStageId];
      const dealIndex = fromStage?.deals?.findIndex(deal => deal.id === dealId);

      if (dealIndex >= 0) {
        const deal = fromStage.deals[dealIndex];

        // Убираем из старого этапа
        updatedStageDeals[fromStageId] = {
          ...fromStage,
          deals: fromStage.deals.filter(d => d.id !== dealId),
          count: fromStage.count - 1,
          total_amount: fromStage.total_amount - (deal.amount || 0)
        };

        // Добавляем в новый этап
        const toStage = updatedStageDeals[toStageId] || { deals: [], count: 0, total_amount: 0 };
        const updatedDeal = { ...deal, stageId: toStageId };

        updatedStageDeals[toStageId] = {
          ...toStage,
          deals: [...(toStage.deals || []), updatedDeal],
          count: toStage.count + 1,
          total_amount: toStage.total_amount + (deal.amount || 0)
        };

        setStageDeals(updatedStageDeals);
      }

      // Отправляем запрос на сервер
      await api.post('/pipelines/move-deal', {
        dealId,
        fromStageId,
        toStageId,
        pipelineId: pipeline.id
      });

      // Перезагружаем данные, чтобы подтянуть обновленный client_id после автоконвертации
      await Promise.all([
        loadPipelineData(pipeline?.id),
        loadSupportData()
      ]);

      showSnackbar('Сделка перемещена', 'success');

    } catch (err) {
      console.error('Ошибка при перемещении сделки:', err);
      showSnackbar('Ошибка при перемещении сделки', 'error');
      // Перезагружаем данные при ошибке
      loadPipelineData(pipeline?.id);
    }
  };

  // ✅ SSE обновления теперь через useRealtimeSSE() hook (строка 42)
  // Старый код удален чтобы избежать дублирования подключений

  const handleAddDeal = (stage) => {
    setDealFormData({
      title: '',
      leadId: '',
      client_id: '',
      product_id: products[0]?.id || '',
      quantity: 1,
      amount: products[0]?.price || 0,
      status: 'new'
    });
    setDealDialog({ open: true, deal: null, stageId: stage.id });
  };

  const handleEditDeal = (deal) => {
    setDealFormData({
      title: deal.title,
      leadId: deal.leadId || '',
      client_id: deal.client_id,
      product_id: deal.product_id,
      quantity: deal.quantity,
      amount: deal.amount,
      status: deal.status
    });
    setDealDialog({ open: true, deal, stageId: deal.stageId });
  };

  const handleDeleteDeal = async (deal) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту сделку?')) {
      return;
    }

    try {
      await api.delete(`/deals/${deal.id}`);
      showSnackbar('Сделка удалена', 'success');
      loadPipelineData(pipeline?.id);
    } catch (err) {
      console.error('Ошибка при удалении сделки:', err);
      showSnackbar('Ошибка при удалении сделки', 'error');
    }
  };

  const handleDealFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const dealData = {
        ...dealFormData,
        amount: parseFloat(dealFormData.amount),
        quantity: parseInt(dealFormData.quantity),
        pipelineId: pipeline.id,
        stageId: dealDialog.stageId
      };

      if (dealDialog.deal) {
        // Редактирование
        await api.put(`/deals/${dealDialog.deal.id}`, dealData);
        showSnackbar('Сделка обновлена', 'success');
      } else {
        // Создание
        await api.post('/deals', dealData);
        showSnackbar('Сделка создана', 'success');
      }

      setDealDialog({ open: false, deal: null, stageId: null });
      loadPipelineData(pipeline?.id);

    } catch (err) {
      console.error('Ошибка при сохранении сделки:', err);
      showSnackbar('Ошибка при сохранении сделки', 'error');
    }
  };

  const handleDealFormChange = (e) => {
    const { name, value } = e.target;
    setDealFormData(prev => {
      let newData = { ...prev, [name]: value };

      // Автоматически пересчитываем сумму при изменении продукта или количества
      if (name === 'product_id') {
        const product = products.find(p => p.id.toString() === value);
        if (product) {
          newData.amount = product.price * parseInt(prev.quantity || 1);
        }
      } else if (name === 'quantity') {
        const product = products.find(p => p.id.toString() === prev.product_id);
        if (product) {
          newData.amount = product.price * parseInt(value || 1);
        }
      }

      return newData;
    });
  };

  const handleStageFormChange = (e) => {
    const { name, value } = e.target;
    setStageForm((prev) => ({
      ...prev,
      [name]: name === 'probability' ? Math.max(0, Math.min(100, Number(value))) : value
    }));
  };

  const openCreateStage = () => {
    setStageForm({ name: '', probability: 0, description: '' });
    setStageDialog({ open: true, mode: 'create', stageId: null });
  };

  const handleEditStage = (stage) => {
    setStageForm({
      name: stage.name || '',
      probability: Number(stage.probability || 0),
      description: stage.description || ''
    });
    setStageDialog({ open: true, mode: 'edit', stageId: stage.id });
  };

  const handleDeleteStage = async (stage) => {
    if (!pipeline?.id) return;
    if (!window.confirm(`Удалить этап "${stage.name}"?`)) return;
    try {
      await api.delete(`/pipelines/${pipeline.id}/stages/${stage.id}`);
      await loadPipelineData(pipeline.id);
      showSnackbar('Этап удален', 'success');
    } catch (e) {
      console.error(e);
      showSnackbar('Ошибка удаления этапа', 'error');
    }
  };

  const handleStageSubmit = async (e) => {
    e.preventDefault();
    if (!pipeline?.id) return;
    try {
      if (stageDialog.mode === 'create') {
        await api.post(`/pipelines/${pipeline.id}/stages`, {
          name: stageForm.name?.trim() || 'Новый этап',
          description: stageForm.description || '',
          probability: Number(stageForm.probability) || 0
        });
      } else {
        // Редактирование: обновляем массив stages и отправляем PATCH /pipelines/:id
        const updatedStages = (stages || []).map((s) =>
          s.id === stageDialog.stageId
            ? { ...s, name: stageForm.name?.trim() || s.name, description: stageForm.description || '', probability: Number(stageForm.probability) || 0 }
            : s
        );
        await api.patch(`/pipelines/${pipeline.id}`, { stages: updatedStages });
      }
      setStageDialog({ open: false, mode: 'create', stageId: null });
      await loadPipelineData(pipeline.id);
      showSnackbar('Этап сохранен', 'success');
    } catch (e) {
      console.error(e);
      showSnackbar('Ошибка сохранения этапа', 'error');
    }
  };

  const handleAnalyticsFilterChange = (e) => {
    const { name, value } = e.target;
    setAnalyticsFilters(prev => ({ ...prev, [name]: value }));
  };

  const loadAnalytics = async () => {
    if (!pipeline?.id) return;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const params = {};
      if (analyticsFilters.dateFrom) params.dateFrom = analyticsFilters.dateFrom;
      if (analyticsFilters.dateTo) params.dateTo = analyticsFilters.dateTo;
      const res = await api.get(`/pipelines/${pipeline.id}/stats`, { params });
      setAnalyticsData(res.data);
    } catch (e) {
      console.error('Ошибка аналитики:', e);
      setAnalyticsError('Не удалось загрузить аналитику');
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const formatHours = (h) => {
    if (!h || isNaN(h)) return '0 ч';
    return `${Math.round(h)} ч`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getTotalStats = () => {
    const totalDeals = Object.values(stageDeals).reduce((sum, stage) => sum + (stage.count || 0), 0);
    const totalAmount = Object.values(stageDeals).reduce((sum, stage) => sum + (stage.total_amount || 0), 0);

    return { totalDeals, totalAmount };
  };

  if (authLoading || !currentOrg) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const stats = getTotalStats();

  // Обработка drag-and-drop
  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    // Если нет места назначения, ничего не делаем
    if (!destination) {
      return;
    }

    // Если элемент остался на том же месте, ничего не делаем
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Перемещаем сделку
    handleDealDrop(draggableId, source.droppableId, destination.droppableId);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Layout>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Заголовок и статистика */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr auto auto' },
              alignItems: 'center',
              columnGap: 3,
              rowGap: 2,
              mb: 3,
              p: { xs: 2, md: 3 },
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '15px',
              boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flexDirection: { xs: 'column', md: 'row' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccountTree sx={{ fontSize: { xs: 28, md: 40 }, color: '#3B82F6' }} />
                <Box>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 'bold',
                      mb: 0.5,
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: { xs: '1.3rem', md: '2rem' }
                    }}
                  >
                    {pipeline?.name || 'Воронка продаж'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: { xs: '0.85rem', md: '0.95rem' }
                    }}
                  >
                    {pipeline?.description || 'Управление процессом продаж'}
                  </Typography>
                </Box>
              </Box>

              {/* Выбор воронки */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
                  <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>Воронка</InputLabel>
                  <Select
                    label="Воронка"
                    value={pipeline?.id || ''}
                    onChange={handlePipelineSelect}
                    sx={{ width: { xs: '100%', md: 220 }, color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                  >
                    {pipelines.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.name}{p.isDefault ? ' (по умолчанию)' : ''}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton onClick={openEditPipeline} title="Настройки воронки" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  <Settings />
                </IconButton>
                <Button variant="outlined" startIcon={<Add />} onClick={openCreatePipeline} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>Новая</Button>
              </Box>

              {/* Статистика */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-end' } }}>
                <Box sx={{ textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3B82F6' }}>
                    {stats.totalDeals}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Сделок
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10B981', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                    {formatCurrency(stats.totalAmount)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Общая сумма
                  </Typography>
                </Box>
              </Box>

              {/* Кнопки действий (снизу) */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', gridColumn: '1 / -1', mt: 1 }}>
                <IconButton
                  onClick={() => loadPipelineData(pipeline?.id)}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: 'white' } }}
                >
                  <Refresh />
                </IconButton>
                <Button
                  variant="outlined"
                  startIcon={<Analytics />}
                  onClick={() => { setAnalyticsOpen(true); loadAnalytics(); }}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      background: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Аналитика
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={openCreateStage}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      background: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Новый этап
                </Button>
                <Button
                  variant="outlined"
                  onClick={async () => {
                    try {
                      const resp = await api.get(`/pipelines/${pipeline.id}/export`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([resp.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `pipeline_${pipeline.id}_stats.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      showSnackbar('Экспорт готов', 'success');
                    } catch (e) {
                      showSnackbar('Ошибка экспорта', 'error');
                    }
                  }}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      background: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Экспорт CSV
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Обработка состояний загрузки и ошибок */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              action={
                <Button color="inherit" size="small" onClick={() => loadPipelineData(pipeline?.id)}>
                  Повторить
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  width={320}
                  height={600}
                  sx={{
                    borderRadius: '16px',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }}
                />
              ))}
            </Box>
          ) : stages.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '15px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <TrendingUp sx={{ fontSize: 80, color: 'rgba(255, 255, 255, 0.5)', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2 }}>
                Воронка не настроена
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
                Создайте этапы воронки для управления процессом продаж
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={createDefaultPipeline}
              >
                Создать базовую воронку
              </Button>
            </Box>
          ) : (
            /* Воронка с этапами */
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 2,
                mt: 1,
                alignItems: 'stretch',
                '& > *': { flex: '0 0 auto' },
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                },
              }}
            >
              {stages.map((stage) => (
                <PipelineStage
                  key={stage.id}
                  stage={stage}
                  deals={stageDeals[stage.id]?.deals || []}
                  onDealDrop={handleDealDrop}
                  onDealEdit={handleEditDeal}
                  onDealDelete={handleDeleteDeal}
                  onAddDeal={handleAddDeal}
                  onEditStage={handleEditStage}
                  onDeleteStage={handleDeleteStage}
                  clients={clients}
                  products={products}
                  leads={leads}
                  isLoading={loading}
                />
              ))}
            </Box>
          )}

          {/* Диалог аналитики */}
          <Dialog
            open={analyticsOpen}
            onClose={() => setAnalyticsOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                background: 'rgba(30, 30, 30, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <DialogTitle sx={{ color: 'white' }}>Аналитика по воронке</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Дата с"
                  type="date"
                  name="dateFrom"
                  value={analyticsFilters.dateFrom}
                  onChange={handleAnalyticsFilterChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
                />
                <TextField
                  label="Дата по"
                  type="date"
                  name="dateTo"
                  value={analyticsFilters.dateTo}
                  onChange={handleAnalyticsFilterChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
                />
                <Button variant="outlined" onClick={loadAnalytics} sx={{ color: 'white' }}>Обновить</Button>
              </Box>

              {analyticsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {analyticsError && (
                <Alert severity="error" sx={{ mb: 2 }}>{analyticsError}</Alert>
              )}

              {analyticsData && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>Всего сделок</Typography>
                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>{analyticsData.total_deals}</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>Общая сумма</Typography>
                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>{formatCurrency(analyticsData.total_amount)}</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>Средний чек</Typography>
                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>{formatCurrency(analyticsData.average_deal_size)}</Typography>
                  </Paper>

                  {/* Шкала денег по этапам */}
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}>Сумма по этапам</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {analyticsData.pipeline.stages
                        .slice()
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((stage) => {
                          const s = analyticsData.stage_stats[stage.id];
                          const total = analyticsData.total_amount || 1;
                          const widthPct = total > 0 ? Math.min(100, Math.round((s?.total_amount || 0) / total * 100)) : 0;
                          return (
                            <Box key={stage.id}>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{stage.name} — {formatCurrency(s?.total_amount || 0)}</Typography>
                              <Box sx={{ height: 10, background: 'rgba(255, 255, 255, 0.1)', borderRadius: 5 }}>
                                <Box sx={{ width: `${widthPct}%`, height: '100%', background: stage.color || '#3B82F6', borderRadius: 5 }} />
                              </Box>
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>

                  {/* Конверсия между этапами */}
                  <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}>Конверсия между этапами</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {Object.entries(analyticsData.conversion_rates || {}).map(([k, v]) => (
                        <Paper key={k} sx={{ p: 1, px: 2, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}>
                          <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>{k.replace('_to_', ' → ')}</Typography>
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>{Math.round(v)}%</Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>

                  {/* Среднее время в этапах */}
                  <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}>Среднее время в этапах</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                      {analyticsData.pipeline.stages
                        .slice()
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((stage) => {
                          const s = analyticsData.stage_stats[stage.id];
                          return (
                            <Paper key={stage.id} sx={{ p: 1, px: 2, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}>
                              <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>{stage.name}</Typography>
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>{formatHours(s?.avg_time_in_stage_hours)}</Typography>
                            </Paper>
                          );
                        })}
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAnalyticsOpen(false)} sx={{ color: 'rgba(255,255,255,0.8)' }}>Закрыть</Button>
              <Button
                onClick={async () => {
                  try {
                    const resp = await api.get(`/pipelines/${pipeline.id}/export`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([resp.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `pipeline_${pipeline.id}_stats.csv`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (e) {
                    showSnackbar('Ошибка экспорта', 'error');
                  }
                }}
                sx={{ color: 'white' }}
                variant="contained"
              >Экспорт CSV</Button>
            </DialogActions>
          </Dialog>

          {/* Диалог этапа */}
          <Dialog
            open={stageDialog.open}
            onClose={() => setStageDialog({ open: false, mode: 'create', stageId: null })}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                background: 'rgba(30, 30, 30, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <form onSubmit={handleStageSubmit}>
              <DialogTitle sx={{ color: 'white' }}>{stageDialog.mode === 'create' ? 'Новый этап' : 'Редактировать этап'}</DialogTitle>
              <DialogContent>
                <TextField
                  fullWidth
                  label="Название этапа"
                  name="name"
                  value={stageForm.name}
                  onChange={handleStageFormChange}
                  margin="normal"
                  required
                  sx={{ '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiInputBase-root': { color: 'white' } }}
                />
                <TextField
                  fullWidth
                  label="Описание"
                  name="description"
                  value={stageForm.description}
                  onChange={handleStageFormChange}
                  margin="normal"
                  sx={{ '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiInputBase-root': { color: 'white' } }}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Вероятность, %"
                  name="probability"
                  value={stageForm.probability}
                  onChange={handleStageFormChange}
                  margin="normal"
                  inputProps={{ min: 0, max: 100 }}
                  required
                  sx={{ '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiInputBase-root': { color: 'white' } }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setStageDialog({ open: false, mode: 'create', stageId: null })} sx={{ color: 'rgba(255,255,255,0.8)' }}>Отмена</Button>
                <Button type="submit" variant="contained">Сохранить</Button>
              </DialogActions>
            </form>
          </Dialog>

          {/* Диалог создания/редактирования воронки */}
          <Dialog
            open={pipelineDialog.open}
            onClose={() => setPipelineDialog({ open: false, mode: 'create' })}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { background: 'rgba(30,30,30,0.95)', border: '1px solid rgba(255,255,255,0.2)' } }}
          >
            <form onSubmit={handlePipelineSubmit}>
              <DialogTitle sx={{ color: 'white' }}>{pipelineDialog.mode === 'create' ? 'Новая воронка' : 'Настройки воронки'}</DialogTitle>
              <DialogContent>
                <TextField
                  fullWidth
                  label="Название"
                  name="name"
                  value={pipelineForm.name}
                  onChange={handlePipelineFormChange}
                  margin="normal"
                  required
                  sx={{ '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiInputBase-root': { color: 'white' } }}
                />
                <TextField
                  fullWidth
                  label="Описание"
                  name="description"
                  value={pipelineForm.description}
                  onChange={handlePipelineFormChange}
                  margin="normal"
                  sx={{ '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiInputBase-root': { color: 'white' } }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <label style={{ color: 'rgba(255,255,255,0.8)' }}>
                    <input type="checkbox" name="isDefault" checked={pipelineForm.isDefault} onChange={handlePipelineFormChange} style={{ marginRight: 8 }} />
                    Сделать по умолчанию
                  </label>
                </Box>
              </DialogContent>
              <DialogActions>
                {pipelineDialog.mode === 'edit' && (
                  <Button onClick={handlePipelineDelete} sx={{ color: '#EF4444' }}>Удалить</Button>
                )}
                <Button onClick={() => setPipelineDialog({ open: false, mode: 'create' })} sx={{ color: 'rgba(255,255,255,0.8)' }}>Отмена</Button>
                <Button type="submit" variant="contained">Сохранить</Button>
              </DialogActions>
            </form>
          </Dialog>

          {/* Диалог создания/редактирования сделки */}
          <Dialog
            open={dealDialog.open}
            onClose={() => setDealDialog({ open: false, deal: null, stageId: null })}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                background: 'rgba(30, 30, 30, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            <form onSubmit={handleDealFormSubmit}>
              <DialogTitle sx={{ color: 'white' }}>
                {dealDialog.deal ? 'Редактировать сделку' : 'Новая сделка'}
              </DialogTitle>
              <DialogContent>
                <TextField
                  fullWidth
                  label="Название сделки"
                  name="title"
                  value={dealFormData.title}
                  onChange={handleDealFormChange}
                  margin="normal"
                  required
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputBase-root': { color: 'white' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  }}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Лид (опционально)</InputLabel>
                  <Select
                    name="leadId"
                    value={dealFormData.leadId}
                    onChange={handleDealFormChange}
                    label="Лид (опционально)"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  >
                    <MenuItem value="">
                      Без лида
                    </MenuItem>
                    {(Array.isArray(leads) ? leads : []).map((lead) => (
                      <MenuItem key={lead.id} value={lead.id}>
                        {lead.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Продукт</InputLabel>
                  <Select
                    name="product_id"
                    value={dealFormData.product_id}
                    onChange={handleDealFormChange}
                    label="Продукт"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  >
                    {products.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name} - {formatCurrency(product.price)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Количество"
                  name="quantity"
                  type="number"
                  value={dealFormData.quantity}
                  onChange={handleDealFormChange}
                  margin="normal"
                  required
                  inputProps={{ min: 1, step: 1 }}
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputBase-root': { color: 'white' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  }}
                />

                <TextField
                  fullWidth
                  label="Сумма"
                  name="amount"
                  type="number"
                  value={dealFormData.amount}
                  onChange={handleDealFormChange}
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputBase-root': { color: 'white' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setDealDialog({ open: false, deal: null, stageId: null })}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                >
                  {dealDialog.deal ? 'Сохранить' : 'Создать'}
                </Button>
              </DialogActions>
            </form>
          </Dialog>

          {/* Snackbar для уведомлений */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Layout>
    </DragDropContext>
  );
};

export default PipelinePage;
